import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, effect, inject, input, output, signal } from '@angular/core';

import { Shared } from '@shared/exports';

import type { SelectItem } from '@appTypes';
import { MultiagendaService, SeguimientoReprogramar, FechaHora } from '../multiagenda.service';

@Component({
  selector: 'app-multiagenda-day',
  standalone: true,
  imports: [
    CommonModule,
    Shared
  ],
  templateUrl: './multiagenda-day.html',
  styleUrl: './multiagenda-day.scss'
})
export class MultiagendaDay implements AfterViewInit {
  private readonly multiagendaService = inject(MultiagendaService);

  // Inputs para configuración
  $idDistribuidor = input<string>('', { alias: 'idDistribuidor' });
  $idPropietario = input<string>('', { alias: 'idPropietario' });
  $idSeguimiento = input<string>('', { alias: 'idSeguimiento' });

  // Inputs para datos del padre (modo controlado)
  $seguimientosInput = input<SeguimientoReprogramar[] | null>(null, { alias: 'seguimientos' });
  $fechaInput = input<Date | null>(null, { alias: 'fecha' });

  // Output para notificar cambios de fecha al padre
  fechaChange = output<Date>();

  // ============ SEGUIMIENTOS ============
  seguimientos = signal<SeguimientoReprogramar[]>([]);
  loadingSeguimientos = signal<boolean>(false);

  // Flag para saber si el padre controla los datos
  private get modoControlado(): boolean {
    return this.$seguimientosInput() !== null;
  }

  constructor() {
    // Reaccionar a cambios en seguimientos del padre
    effect(() => {
      const seguimientosDelPadre = this.$seguimientosInput();
      console.log('Effect seguimientos - recibidos del padre:', seguimientosDelPadre);
      if (seguimientosDelPadre !== null) {
        this.seguimientos.set(seguimientosDelPadre);
        console.log('Effect seguimientos - actualizados:', this.seguimientos().length);
      }
    });

    // Reaccionar a cambios en fecha del padre
    effect(() => {
      const fechaDelPadre = this.$fechaInput();
      console.log('Effect fecha - recibida del padre:', fechaDelPadre);
      // Validar que la fecha sea válida antes de usarla
      if (fechaDelPadre && !isNaN(fechaDelPadre.getTime())) {
        this.currentDate.set(fechaDelPadre);
        this.selectedDate.set(fechaDelPadre);
      }
    });
  }

  // ============ CALENDARIO ============
  currentDate = signal<Date>(new Date());
  selectedDate = signal<Date>(new Date());

  // Horas del día (7:00 - 21:00)
  dayHours = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

  // ============ FECHA MÍNIMA (HOY) ============
  private readonly today = new Date();
  private readonly todayStart = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());

  // ============ SELECTORES DE MES Y AÑO ============
  private readonly allMonthOptions: SelectItem[] = [
    { key: '0', value: 'Enero' },
    { key: '1', value: 'Febrero' },
    { key: '2', value: 'Marzo' },
    { key: '3', value: 'Abril' },
    { key: '4', value: 'Mayo' },
    { key: '5', value: 'Junio' },
    { key: '6', value: 'Julio' },
    { key: '7', value: 'Agosto' },
    { key: '8', value: 'Septiembre' },
    { key: '9', value: 'Octubre' },
    { key: '10', value: 'Noviembre' },
    { key: '11', value: 'Diciembre' },
  ];

  // Opciones de mes filtradas (no mostrar meses pasados si es el año actual)
  monthOptions = computed<SelectItem[]>(() => {
    const selectedYear = this.currentDate().getFullYear();
    const todayYear = this.todayStart.getFullYear();
    const todayMonth = this.todayStart.getMonth();

    if (selectedYear > todayYear) {
      return this.allMonthOptions;
    } else if (selectedYear === todayYear) {
      return this.allMonthOptions.filter(m => parseInt(String(m.key)) >= todayMonth);
    }
    return [];
  });

  // Opciones de año (solo desde el año actual en adelante)
  yearOptions = computed<SelectItem[]>(() => {
    const todayYear = this.todayStart.getFullYear();
    const years: SelectItem[] = [];
    for (let y = todayYear; y <= todayYear + 5; y++) {
      years.push({ key: y.toString(), value: y.toString() });
    }
    return years;
  });

  currentMonth = computed(() => this.currentDate().getMonth().toString());
  currentYear = computed(() => this.currentDate().getFullYear().toString());
  currentDay = computed(() => this.selectedDate().getDate().toString());

  // Opciones de días del mes actual (filtradas para no mostrar días pasados)
  dayOptions = computed<SelectItem[]>(() => {
    const date = this.currentDate();
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: SelectItem[] = [];

    const todayYear = this.todayStart.getFullYear();
    const todayMonth = this.todayStart.getMonth();
    const todayDay = this.todayStart.getDate();

    // Determinar desde qué día empezar
    let startDay = 1;
    if (year === todayYear && month === todayMonth) {
      startDay = todayDay; // Solo mostrar desde hoy en adelante
    } else if (year < todayYear || (year === todayYear && month < todayMonth)) {
      return []; // Mes pasado, no mostrar días
    }

    for (let d = startDay; d <= daysInMonth; d++) {
      days.push({ key: d.toString(), value: d.toString() });
    }
    return days;
  });

  // Título del día seleccionado (ej: "Martes 13 de Enero de 2026")
  selectedDayTitle = computed(() => {
    const date = this.selectedDate();
    const dayName = date.toLocaleDateString('es-ES', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('es-ES', { month: 'long' });
    const year = date.getFullYear();

    const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

    return `${capitalizedDay} ${day} de ${capitalizedMonth} de ${year}`;
  });

  ngAfterViewInit(): void {
    // Solo cargar si no está en modo controlado por el padre
    if (!this.modoControlado) {
      setTimeout(() => this.cargarSeguimientos(), 0);
    }
  }

  private cargarSeguimientos(): void {
    // Si el padre controla los datos, no hacer petición propia
    if (this.modoControlado) {
      return;
    }

    const idDist = this.$idDistribuidor();
    const idProp = this.$idPropietario();
    const idSeg = this.$idSeguimiento();

    if (!idDist || !idProp || !idSeg) {
      return;
    }

    const selected = this.selectedDate();
    const year = selected.getFullYear();
    const month = selected.getMonth() + 1;
    const day = selected.getDate();

    const fechaStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    this.loadingSeguimientos.set(true);

    this.multiagendaService.getReprogramarSeguimiento({
      idDistribuidor: idDist,
      idPropietario: idProp,
      idSeguimiento: idSeg,
      fechaInicio: fechaStr,
      fechaFin: fechaStr
    }).subscribe({
      next: (resp) => {
        console.log('Respuesta de seguimientos:', resp);
        if (resp?.success && Array.isArray(resp?.seguimientos)) {
          console.log('Seguimientos encontrados:', resp.seguimientos.length);
          resp.seguimientos.forEach((s: SeguimientoReprogramar) => {
            const fecha = this.fechaHoraToDate(s.Programada);
            console.log('Seguimiento:', s.Prospecto, 'Hora:', fecha.getHours(), 'Minutos:', fecha.getMinutes());
          });
          this.seguimientos.set(resp.seguimientos);
          console.log('Signal seguimientos actualizado:', this.seguimientos().length);
        } else {
          this.seguimientos.set([]);
        }
        this.loadingSeguimientos.set(false);
      },
      error: (err) => {
        console.error('Error cargando seguimientos:', err);
        this.seguimientos.set([]);
        this.loadingSeguimientos.set(false);
      }
    });
  }

  // Helper para convertir FechaHora a Date
  private fechaHoraToDate(fh: FechaHora): Date {
    return new Date(
      fh.date.year,
      fh.date.month - 1,
      fh.date.day,
      fh.time.hour,
      fh.time.minute,
      fh.time.second
    );
  }

  // Obtener seguimientos para una hora específica
  // Los datos ya vienen filtrados por fecha del API, solo filtramos por hora
  getSeguimientosForHour(hour: number): SeguimientoReprogramar[] {
    return this.seguimientos().filter(s => {
      const fecha = this.fechaHoraToDate(s.Programada);
      return fecha.getHours() === hour;
    });
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }

  // Formatear hora
  formatHour(hour: number): string {
    return `${hour}h`;
  }

  formatHourHalf(hour: number): string {
    return `${hour}h30`;
  }

  // Formatear hora de un seguimiento
  formatSeguimientoHora(s: SeguimientoReprogramar): string {
    const fecha = this.fechaHoraToDate(s.Programada);
    return `${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;
  }

  // ============ NAVEGACIÓN ============
  // Verificar si una fecha es válida (no anterior a hoy)
  private esFechaValida(fecha: Date): boolean {
    const fechaStart = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    return fechaStart >= this.todayStart;
  }

  // Verificar si se puede ir al día anterior
  canGoPreviousDay = computed(() => {
    const current = this.selectedDate();
    const previousDate = new Date(current);
    previousDate.setDate(current.getDate() - 1);
    return this.esFechaValida(previousDate);
  });

  private actualizarFechaYNotificar(newDate: Date): void {
    // Validar que la fecha no sea anterior a hoy
    if (!this.esFechaValida(newDate)) {
      console.warn('No se puede seleccionar una fecha anterior a hoy');
      return;
    }

    this.currentDate.set(newDate);
    this.selectedDate.set(newDate);

    if (this.modoControlado) {
      // Notificar al padre para que actualice los datos
      this.fechaChange.emit(newDate);
    } else {
      // Cargar datos propios
      this.cargarSeguimientos();
    }
  }

  onMonthChange(monthKey: string): void {
    const current = this.currentDate();
    const newMonth = parseInt(monthKey);
    const daysInNewMonth = new Date(current.getFullYear(), newMonth + 1, 0).getDate();

    // Determinar el día a usar
    let day = Math.min(this.selectedDate().getDate(), daysInNewMonth);

    // Si es el mes actual, asegurar que el día no sea anterior a hoy
    if (current.getFullYear() === this.todayStart.getFullYear() && newMonth === this.todayStart.getMonth()) {
      day = Math.max(day, this.todayStart.getDate());
    }

    const newDate = new Date(current.getFullYear(), newMonth, day);
    this.actualizarFechaYNotificar(newDate);
  }

  onYearChange(yearKey: string): void {
    const current = this.currentDate();
    const newYear = parseInt(yearKey);
    let month = current.getMonth();

    // Si es el año actual, asegurar que el mes no sea anterior al mes actual
    if (newYear === this.todayStart.getFullYear() && month < this.todayStart.getMonth()) {
      month = this.todayStart.getMonth();
    }

    const daysInMonth = new Date(newYear, month + 1, 0).getDate();
    let day = Math.min(this.selectedDate().getDate(), daysInMonth);

    // Si es el mes y año actual, asegurar que el día no sea anterior a hoy
    if (newYear === this.todayStart.getFullYear() && month === this.todayStart.getMonth()) {
      day = Math.max(day, this.todayStart.getDate());
    }

    const newDate = new Date(newYear, month, day);
    this.actualizarFechaYNotificar(newDate);
  }

  onDayChange(dayKey: string): void {
    const current = this.currentDate();
    const newDate = new Date(current.getFullYear(), current.getMonth(), parseInt(dayKey));
    this.actualizarFechaYNotificar(newDate);
  }

  previousDay(): void {
    const current = this.selectedDate();
    const newDate = new Date(current);
    newDate.setDate(current.getDate() - 1);

    // Validar antes de actualizar
    if (this.esFechaValida(newDate)) {
      this.actualizarFechaYNotificar(newDate);
    }
  }

  nextDay(): void {
    const current = this.selectedDate();
    const newDate = new Date(current);
    newDate.setDate(current.getDate() + 1);
    this.actualizarFechaYNotificar(newDate);
  }

  goToToday(): void {
    const today = new Date();
    this.actualizarFechaYNotificar(today);
  }
}
